import z from "zod"

export type SquadupEvents = {
  [K in keyof typeof SQUADUP_EVENT_TYPES]: z.infer<(typeof SQUADUP_EVENT_TYPES)[K]>
}

export type SquadupEventName = keyof SquadupEvents

export type SquadupDispatchedEvent = {
  [K in keyof SquadupEvents]: {
    type: K
    data: SquadupEvents[K]
  }
}[keyof SquadupEvents]

export type SquadupEventHandler = <TName extends SquadupEventName>(eventName: TName, data: SquadupEvents[TName]) => void

const PriceTier = z.object({
  id: z.number(),
  price: z.string(),
  name: z.string(),
  quantity: z.number(),
  quantity_sold: z.number(),
  fees_paid_by: z.string(),
  min_quantity: z.string().nullable(),
  max_quantity: z.string().nullable(),
  qty_limit: z.string().nullable(),
  min_order_qty: z.string().nullable(),
  sort: z.number(),
  priority: z.number(),
  remaining_quantity: z.number(),
  description: z.string().nullable(),
  sales_start_date: z.string().nullable(),
  sales_end_date: z.string().nullable(),
  preceding_price_tier_id: z.string().nullable(),
  willcall: z.boolean(),
  eticket: z.boolean(),
  shipped: z.boolean(),
  added_fee_dollar: z.string(),
  added_fee_percent: z.string(),
  coming_soon: z.boolean(),
  hidden: z.boolean(),
  unhide_promo_code_id: z.string().nullable(),
  package: z.boolean(),
  package_quantity: z.string().nullable(),
  default_delivery_method: z.string(),
  event_id: z.number(),
  allowed_bins: z.array(z.unknown()),
  sold_out: z.boolean(),
  hide_on_sold_out: z.boolean(),
  seats_io_category_key: z.string().nullable(),
  unhide_on_gift_card: z.boolean(),
  is_face_value: z.boolean(),
  add_on: z.boolean(),
  ticketless: z.boolean(),
  squadup_fee_dollar: z.string().nullable(),
  squadup_fee_percent: z.string().nullable(),
  unlock_price_tier_ids: z.array(z.unknown()),
})

export type SquadupPriceTier = z.infer<typeof PriceTier>

const Event = z.object({
  id: z.number(),
  name: z.string(),
  name_line_2: z.string().nullable(),
  url: z.string(),
  hashtag: z.string(),
  image: z.object({
    thumbnail_url: z.string().nullable(),
    default_url: z.string().nullable(),
  }),
  banner_url: z.string().nullable(),
  description: z.string(),
  description_link: z.string(),
  timezone_name: z.string(),
  start_at: z.string(),
  end_at: z.string(),
  rsvp_by: z.string(),
  heading_text_color: z.string(),
  banner_text_color: z.string(),
  topics: z.array(z.unknown()),
  updated_at: z.string(),
  created_at: z.string(),
  fees_waived: z.boolean(),
  ticketed: z.boolean(),
  squadup_fee_dollar: z.string(),
  squadup_fee_percent: z.string(),
  processing_fee_dollar: z.string(),
  processing_fee_percent: z.string(),
  squadup_fee_cap: z.string(),
  added_fee_dollar: z.string(),
  added_fee_percent: z.string(),
  shipping_fee: z.string(),
  tax_rate: z.string(),
  ticket_button_verbage: z.string(),
  confirmation_redirect_url: z.string().nullable(),
  accept_donations: z.boolean(),
  social_discount_facebook: z.string().nullable(),
  social_discount_twitter: z.string().nullable(),
  require_individual_signup: z.boolean(),
  collect_attendee_info_by: z.string(),
  box_office_fee_percent: z.string(),
  box_office_fee_dollar: z.string(),
  box_office_processing_fee_percent: z.string(),
  box_office_processing_fee_dollar: z.string(),
  is_race: z.boolean(),
  privacy: z.string(),
  show_start_date: z.boolean(),
  show_end_date: z.boolean(),
  show_timezone: z.boolean().nullable(),
  donation_charity_name: z.string().nullable(),
  donation_charity_url: z.string().nullable(),
  background_url: z.string().nullable(),
  facebook_tracking_id: z.string().nullable(),
  seats_io_event_key: z.string().nullable(),
  seats_io_public_key: z.string(),
  has_reserved_seating: z.boolean(),
  max_capacity: z.string().nullable(),
  host_pays_tax_processing: z.boolean(),
  allowed_zip_codes: z.array(z.unknown()),
  timezone_abbreviation: z.string(),
  location_type: z.string(),
  promo_codes_available: z.boolean(),
  locale: z.string(),
  currency_code: z.string(),
  has_custom_gateway: z.boolean(),
  gateway_name: z.string(),
  transfers_disabled: z.boolean(),
  host: z.object({
    id: z.number(),
    first_name: z.string(),
    last_name: z.string(),
    name: z.string(),
  }),
  location: z.object({
    id: z.string().nullable(),
    name: z.string(),
    latitude: z.string(),
    longitude: z.string(),
    address_line_1: z.string(),
    address_line_2: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    zip: z.string().nullable(),
    country: z.string().nullable(),
    timezone_name: z.string(),
  }),
  category: z
    .object({
      id: z.number(),
      name: z.string(),
      subcategory: z.boolean().nullable(),
    })
    .nullable(),
  price_tiers: z.array(PriceTier),
  event_dates: z.array(z.null()),
  custom_fields: z.array(z.null()),
  bundles: z.array(z.null()),
})

export type SquadupEventItem = z.infer<typeof Event>

const EventTicket = z.object({
  price_tier_id: z.number(),
  quantity: z.number(),
  selected_seats: z.array(z.unknown()),
  priceTier: z.object({
    originalData: z.object({
      id: z.number(),
      price: z.string(),
      name: z.string(),
      quantity: z.number(),
      quantity_sold: z.number(),
      fees_paid_by: z.string(),
      min_quantity: z.null(),
      max_quantity: z.null(),
      qty_limit: z.null(),
      min_order_qty: z.null(),
      sort: z.number(),
      priority: z.number(),
      remaining_quantity: z.number(),
      description: z.string().nullable(),
      sales_start_date: z.null(),
      sales_end_date: z.null(),
      preceding_price_tier_id: z.null(),
      willcall: z.boolean(),
      eticket: z.boolean(),
      shipped: z.boolean(),
      added_fee_dollar: z.string(),
      added_fee_percent: z.string(),
      coming_soon: z.boolean(),
      hidden: z.boolean(),
      unhide_promo_code_id: z.null(),
      package: z.boolean(),
      package_quantity: z.null(),
      default_delivery_method: z.string(),
      event_id: z.number(),
      allowed_bins: z.array(z.unknown()),
      sold_out: z.boolean(),
      hide_on_sold_out: z.boolean(),
      seats_io_category_key: z.null(),
      unhide_on_gift_card: z.boolean(),
      is_face_value: z.boolean(),
      add_on: z.boolean(),
      ticketless: z.boolean(),
      squadup_fee_dollar: z.null(),
      squadup_fee_percent: z.null(),
      unlock_price_tier_ids: z.array(z.unknown()),
    }),
    id: z.number(),
    name: z.string(),
    description: z.string().nullable(),
    price: z.number(),
    quantity: z.number(),
    qty_available: z.number(),
    qty_limit: z.null(),
    min_qty: z.number(),
    fee_delegation: z.string(),
    priority: z.number(),
    eticket: z.boolean(),
    shipped: z.boolean(),
    willcall: z.boolean(),
    ticketless: z.boolean(),
    event_date_id: z.null(),
    onlyETicket: z.boolean(),
    addedFeeDollar: z.number(),
    addedFeePercent: z.number(),
    delivery_method: z.string(),
    coming_soon: z.boolean(),
    sold_out: z.boolean(),
    hidden: z.boolean(),
    unhide_promo_code_id: z.null(),
    isPackage: z.boolean(),
    packageQuantity: z.number(),
    sales_start_date: z.null(),
    allowed_bins: z.array(z.unknown()),
    promoQtyApplied: z.number(),
    promoLimited: z.boolean(),
    addOn: z.boolean(),
    unlockPriceTierIds: z.array(z.unknown()),
    seats_io_category_key: z.null(),
    squadupFeeDollar: z.number(),
    squadupFeePercent: z.number(),
    quantitySelected: z.number(),
    event: Event,
  }),
  deliveryMethod: z.string(),
})

export type SquadupEventTicket = z.infer<typeof EventTicket>

const EventLoadedSchema = z.object({
  event: Event,
})

const ViewName = z.enum(["eventDetail", "eventList", "seatingChart", "checkout"])

const ViewChangedSchema = z.object({
  currentView: ViewName,
})

export type SquadupViewName = z.infer<typeof ViewName>

const SquadupShoppingCartItem = z.object({
  event: Event,
  tickets: z.array(EventTicket),
  questions: z.array(z.unknown()),
  transfers: z.array(z.unknown()),
  donation: z.string(),
  hasShippedTicket: z.boolean(),
  promotion: z.object({
    type: z.null(),
    alertSuccess: z.null(),
    alertInfo: z.null(),
    alertWarning: z.null(),
    alertDanger: z.null(),
    originalPromo: z.null(),
    appliedPromo: z.null(),
  }),
  giftCard: z.object({
    message: z.null(),
    giftCardObject: z.null(),
    balance: z.null(),
  }),
  socialDiscount: z.object({
    facebook: z.object({
      discount: z.number(),
      popup: z.null(),
      popupShown: z.boolean(),
      applied: z.boolean(),
    }),
    twitter: z.object({
      discount: z.number(),
      popup: z.null(),
      popupShown: z.boolean(),
      applied: z.boolean(),
    }),
  }),
})

export type SquadupShoppingCartItem = z.infer<typeof SquadupShoppingCartItem>

const ShoppingCartUpdatedSchema = z.array(SquadupShoppingCartItem)

const ShoppingCartQuantityUpdatedSchema = z.number()

const OrderSuccessSchema = z.object({
  items: z.array(
    z.object({
      event: z.object({
        id: z.number(),
        name: z.string(),
        host: z.object({
          id: z.number(),
          first_name: z.string(),
          last_name: z.string(),
        }),
      }),
      donation: z.number(),
      tickets: z.array(
        z.object({
          id: z.number(),
          quantity: z.number(),
          delivery_method: z.string(),
          price: z.number(),
          promotion_amount: z.number(),
          name: z.string(),
        }),
      ),
      custom_fields: z.array(
        z.object({
          question_id: z.number(),
          price_tier_id: z.string(),
          answer: z.string(),
        }),
      ),
      order_id: z.number(),
      amount: z.string(),
      promotion_amount: z.string(),
      transaction_id: z.null(),
      promotion: z.object({}),
      toneden_face_value: z.string(),
    }),
  ),
  total: z.number(),
  tonedenFaceValue: z.number(),
})

export type OrderSuccess = z.infer<typeof OrderSuccessSchema>

export const SQUADUP_EVENT_TYPES = {
  eventLoaded: EventLoadedSchema,
  viewChanged: ViewChangedSchema,
  createSquadupEmbed: z.string().nullable(),
  destroySquadupEmbed: z.string().nullable(),
  shoppingCartUpdated: ShoppingCartUpdatedSchema,
  shoppingCartQuantityUpdated: ShoppingCartQuantityUpdatedSchema,
  orderSuccessful: OrderSuccessSchema,
  "dismissableLayer.update": z.any(),
} satisfies { [eventName: string]: z.Schema<any> }
